// src/pages/public/LegacyMagnet/LegacyMagnetEdit.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from '../../../public/icons';
import { MinusIcon, PlusIcon, GiftIcon } from '../../../public/icons';
import PersonalizeModal from '../../../component/legacymagnet/PersonalizeModal';
import ReviewModal from '../../../component/general/ReviewModal';
import CustomerReviews from '../../../component/general/CustomerReviews';
import CustomizeModal from '../../../component/legacymagnet/CustomizeModal';

/* ─────────────────────────────────────────────
   Draft persistence helpers
   ───────────────────────────────────────────── */
const DRAFT_PREFIX = 'kapta_draft_';
const MODAL_DRAFT_KEY = 'kapta_legacy_modal_draft';

const loadDraft = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (err) {
    console.error('Failed to load draft:', err);
    return null;
  }
};

const saveDraft = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch (err) {
    console.error('Failed to save draft:', err);
  }
};

const clearDraftByKey = (key) => {
  try {
    localStorage.removeItem(key);
  } catch (err) {
    console.error('Failed to clear draft:', err);
  }
};

const STEP_KEYS = {
  customize: 'kapta_legacy_step_customize',
  personalize: 'kapta_legacy_step_personalize',
};

const clearStepDrafts = () => {
  Object.values(STEP_KEYS).forEach((k) => localStorage.removeItem(k));
};

const LegacyMagnetEdit = () => {
  const location = useLocation();

  /* ── Stable draft key from URL (survives reload) ── */
  const draftKey =
    `${DRAFT_PREFIX}${location.pathname
      .replace(/^\//, '')
      .replace(/\//g, '-')
      .replace(/-+/g, '-')}` ||
    `${DRAFT_PREFIX}legacy-magnet_default`;

  const [quantity, setQuantity] = useState(1);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [personalizedMessage, setPersonalizedMessage] = useState('');
  const [showCustomizeModal, setShowCustomizeModal] = useState(false);
  const [showPersonalizeModal, setShowPersonalizeModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [customizationData, setCustomizationData] = useState(null);

  const [reviewsRefreshKey, setReviewsRefreshKey] = useState(0);
  const [isAdding, setIsAdding] = useState(false);

  const [toast, setToast] = useState({
    show: false,
    message: '',
    type: 'success'
  });

  const draftHydratedRef = useRef(false);

  /* ───────── HYDRATE on mount ───────── */
  useEffect(() => {
    const draft = loadDraft(draftKey);
    if (!draft) {
      draftHydratedRef.current = true;
      return;
    }

    if (typeof draft.quantity === 'number') setQuantity(draft.quantity);
    if (typeof draft.currentImageIndex === 'number')
      setCurrentImageIndex(draft.currentImageIndex);
    if (typeof draft.personalizedMessage === 'string')
      setPersonalizedMessage(draft.personalizedMessage);
    if (draft.customizationData !== undefined)
      setCustomizationData(draft.customizationData);

    draftHydratedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftKey]);

  /* ───────── AUTOSAVE on every change ───────── */
  useEffect(() => {
    if (!draftHydratedRef.current) return;

    saveDraft(draftKey, {
      quantity,
      currentImageIndex,
      personalizedMessage,
      customizationData
    });
  }, [
    draftKey,
    quantity,
    currentImageIndex,
    personalizedMessage,
    customizationData
  ]);

  /* Save on refresh / tab close */
  useEffect(() => {
    const saveNow = () => {
      if (!draftHydratedRef.current) return;
      saveDraft(draftKey, {
        quantity,
        currentImageIndex,
        personalizedMessage,
        customizationData
      });
    };
    window.addEventListener('beforeunload', saveNow);
    return () => window.removeEventListener('beforeunload', saveNow);
  }, [
    draftKey,
    quantity,
    currentImageIndex,
    personalizedMessage,
    customizationData
  ]);

  const productImages = [
    'https://images.unsplash.com/photo-1544716278-ca5e3f4abd8c?w=800&auto=format&fit=crop&q=60',
    'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=800&auto=format&fit=crop&q=60',
    'https://images.unsplash.com/photo-1513475382585-d06e58bcb0e0?w-800&auto=format&fit=crop&q=60',
    'https://images.unsplash.com/photo-1512820790803-83ca734da794?w=800&auto=format&fit=crop&q=60'
  ];

  const title = 'Legacy Magnet Book';
  const price = 30000;
  const inStock = true;
  const PRODUCT_SLUG = 'legacy-magnet-book';

  /* ───────── Toast helper ───────── */
  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: '', type: 'success' });
    }, 3000);
  };

  /* ───────── Reset the modal flow ───────── */
  const resetFlow = () => {
    setCustomizationData(null);
    setQuantity(1);
    setPersonalizedMessage('');
  };

  /* ───────── Add to bag ───────── */
  const handleAddToBag = async (magnetData) => {
    if (isAdding) return;

    if (!inStock) {
      showToast('This item is out of stock', 'error');
      return;
    }

    try {
      setIsAdding(true);

      await new Promise((resolve) => setTimeout(resolve, 400));

      const cartItem = {
        cartItemId: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        id: PRODUCT_SLUG,
        slug: PRODUCT_SLUG,
        title: magnetData.title || `${title} - Custom Set`,
        description: 'A story you can hold, open, and revisit.',
        isCustom: true,
        quantity: magnetData.quantity || quantity,
        price: magnetData.price || price,
        lineTotal:
          (magnetData.price || price) *
          (magnetData.quantity || quantity),
        image: magnetData.image || productImages[0],

        magnetId: magnetData.magnetId || null,
        magnetName: magnetData.magnetName || '',
        collectionName: magnetData.collectionName || '',

        customization: magnetData.customization || {},
        magnets: magnetData.customization?.magnets || [],
        personalizedMessage: magnetData.personalizedMessage || ''
      };

      const existing = JSON.parse(
        localStorage.getItem('memoryMagnetsCart') || '[]'
      );
      existing.push(cartItem);
      localStorage.setItem('memoryMagnetsCart', JSON.stringify(existing));

      /* 🟢 Clear ALL drafts — committed to cart */
      clearDraftByKey(draftKey);
      localStorage.removeItem(MODAL_DRAFT_KEY);
      clearStepDrafts();

      /* 🟢 Notify the app + open the shopping bag */
      window.dispatchEvent(new Event('cartUpdated'));
      window.dispatchEvent(new CustomEvent('openShoppingBag'));

      showToast('Added to bag', 'success');
      setShowPersonalizeModal(false);
      resetFlow();
    } catch (err) {
      console.error('Failed to add to bag:', err);
      showToast('Failed to add to bag. Please try again.', 'error');
    } finally {
      setIsAdding(false);
    }
  };

  /* ───────── Customization flow ───────── */
  const handleCustomizeContinue = (data) => {
    setCustomizationData(data);
    setShowCustomizeModal(false);
    setShowPersonalizeModal(true);
  };

  const handlePersonalizeSave = (personalizedData) => {
    const customization = {
      ...(customizationData || {}),
      ...(personalizedData || {}),
      magnets:
        personalizedData?.magnets ||
        customizationData?.magnets ||
        []
    };

    const setSize = customization.setSize || '12';
    const unitPrice =
      setSize === '16' ? 325000 : setSize === '12' ? 300000 : price;

    const finalProductData = {
      title: `${title} - ${setSize} set`,
      isCustom: true,
      quantity,
      price: unitPrice,
      lineTotal: unitPrice * quantity,
      image: productImages[0],
      customization,
      personalizedMessage: customization.personalNote || ''
    };

    handleAddToBag(finalProductData);
  };

  /* ───────── Progress bridge for step modals ───────── */
  const handleCustomizeProgress = (progress) => {
    if (!progress) return;
    try {
      localStorage.setItem(
        STEP_KEYS.customize,
        JSON.stringify(progress)
      );
    } catch (err) {
      console.error('Failed to save customize step draft:', err);
    }
    // Also reflect into outer draft
    setCustomizationData(progress);
  };

  const handlePersonalizeProgress = (progress) => {
    if (!progress) return;
    try {
      localStorage.setItem(
        STEP_KEYS.personalize,
        JSON.stringify(progress)
      );
    } catch (err) {
      console.error('Failed to save personalize step draft:', err);
    }
  };

  /* ───────── Image navigation ───────── */
  const handlePrevImage = () => {
    setCurrentImageIndex((prev) =>
      prev === 0 ? productImages.length - 1 : prev - 1
    );
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) =>
      prev === productImages.length - 1 ? 0 : prev + 1
    );
  };

  /* ───────── Button renderer ───────── */
  const renderAddToBagButton = () => {
    return (
      <button
        onClick={() => setShowCustomizeModal(true)}
        disabled={!inStock || isAdding}
        className={`py-3 sm:py-4 text-base sm:text-lg font-medium w-full sm:w-auto sm:min-w-[280px] md:w-[393px] transition-colors flex items-center justify-center space-x-2 ${
          !inStock || isAdding
            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
            : 'bg-[#D2A146] text-[#FCF7EE] hover:bg-[#C4956A]'
        }`}
      >
        <GiftIcon />
        <span>Customise</span>
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-[#FCF7EE]">
      {/* Toast Popup */}
      {toast.show && (
        <div
          className="fixed top-24 left-1/2 -translate-x-1/2 z-[9999]"
          style={{ animation: 'fadeInDown 0.3s ease-out' }}
        >
          <div
            className={`flex items-center gap-3 px-6 py-4 rounded-lg shadow-xl border ${
              toast.type === 'success'
                ? 'bg-[#E7F4ED] border-[#2F8F5B] text-[#2F8F5B]'
                : toast.type === 'error'
                ? 'bg-[#F4E7E7] border-[#A82121] text-[#A82121]'
                : 'bg-[#E7ECF4] border-[#2F578F] text-[#2F578F]'
            }`}
          >
            {toast.type === 'success' && (
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="12" cy="12" r="10" stroke="#2F8F5B" strokeWidth="2" />
                <path
                  d="M8 12l3 3 5-6"
                  stroke="#2F8F5B"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
            <span className="font-medium text-sm sm:text-base">
              {toast.message}
            </span>
          </div>
        </div>
      )}

      <div className="container mx-auto px-4 sm:px-6 max-w-[85rem] pt-20 sm:pt-24 md:pt-28 lg:pt-32">
        <div className="flex flex-col lg:flex-row justify-between gap-6 md:gap-8 lg:gap-10">
          {/* Left Column — Images */}
          <div className="w-full lg:w-1/2">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex flex-row sm:flex-col gap-2 sm:gap-2.5 order-2 sm:order-1">
                {productImages.map((image, index) => (
                  <div
                    key={index}
                    className={`w-14 h-14 sm:w-16 sm:h-16 cursor-pointer border-2 flex-shrink-0 ${
                      currentImageIndex === index
                        ? 'border-[#D2A146]'
                        : 'border-transparent'
                    }`}
                    onClick={() => setCurrentImageIndex(index)}
                  >
                    <img
                      src={image}
                      alt={`Thumbnail ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>

              <div className="relative overflow-hidden order-1 sm:order-2 flex-1">
                <img
                  src={productImages[currentImageIndex]}
                  alt={title}
                  className="w-full max-w-[498px] h-[300px] sm:h-[400px] md:h-[500px] lg:h-[632px] object-cover mx-auto"
                />

                <button
                  onClick={handlePrevImage}
                  className="absolute left-2 top-1/2 transform -translate-y-1/2 w-8 h-8 sm:w-10 sm:h-10 bg-[#FCF7EE] hover:bg-[#E8E0D5] flex items-center justify-center transition-colors rounded-full border border-[#DBDBDB]"
                >
                  <ArrowLeft size={16} color="#1C2A46" />
                </button>

                <button
                  onClick={handleNextImage}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 w-8 h-8 sm:w-10 sm:h-10 bg-[#FCF7EE] hover:bg-[#E8E0D5] flex items-center justify-center transition-colors rounded-full border border-[#DBDBDB]"
                >
                  <ArrowRight size={16} color="#1C2A46" />
                </button>
              </div>
            </div>
          </div>

          {/* Right Column — Info */}
          <div className="w-full lg:w-1/2">
            <div className="space-y-6 sm:space-y-8 text-left">
              <div>
                <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#1C2A46] mb-2">
                  Legacy Magnet Book
                </h1>
                <p className="font-light text-xs sm:text-sm text-[#55000C] mb-2">
                  A story you can hold, open, and revisit.
                </p>
                <p className="text-sm sm:text-base md:text-lg text-[#6B635D] leading-relaxed">
                  The Legacy Magnet Book is a one-page keepsake album that holds
                  four removable memory magnets. Each magnet features an image
                  on the front and when lifted, reveals the story behind that
                  moment printed on the back.
                </p>
              </div>

              <div>
                <h3 className="text-base sm:text-lg font-medium text-[#1C2A46] mb-2">
                  What Included
                </h3>
                <div className="bg-[#F5F5F5] px-3 sm:px-4 py-4 sm:py-5">
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <span className="text-[#1C2A46] mr-2 sm:mr-3 mt-1">•</span>
                      <span className="text-[#1C2A46] text-sm sm:text-base">
                        Custom-printed magnets
                      </span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#1C2A46] mr-2 sm:mr-3 mt-1">•</span>
                      <span className="text-[#1C2A46] text-sm sm:text-base">
                        Stories printed behind each magnet
                      </span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#1C2A46] mr-2 sm:mr-3 mt-1">•</span>
                      <span className="text-[#1C2A46] text-sm sm:text-base">
                        Detachable display panel
                      </span>
                    </li>
                  </ul>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center justify-between space-x-4 w-32 sm:w-36 p-1.5 border border-[#E0E0E0]">
                    <button
                      onClick={() => setQuantity((prev) => Math.max(1, prev - 1))}
                      className="flex items-center justify-center hover:bg-[#F5F5F5] transition-colors"
                      disabled={isAdding}
                    >
                      <MinusIcon className="w-6 h-6 sm:w-7 sm:h-7 text-[#1C2A46]" />
                    </button>
                    <span className="text-sm sm:text-base font-normal text-[#1C2A46] min-w-[2.5rem] sm:min-w-[3rem] text-center">
                      {quantity} set
                    </span>
                    <button
                      onClick={() => setQuantity((prev) => prev + 1)}
                      className="rounded-lg flex items-center justify-center hover:bg-[#F5F5F5] transition-colors"
                      disabled={isAdding}
                    >
                      <PlusIcon className="w-6 h-6 sm:w-7 sm:h-7 text-[#1C2A46]" />
                    </button>
                  </div>
                </div>

                <p className="text-xs sm:text-sm text-[#6B635D] italic mb-6 sm:mb-7">
                  Each set contains 4 memory magnets
                </p>

                <div className="flex flex-col sm:flex-row gap-4 sm:gap-5">
                  <div className="mb-4 sm:mb-6">
                    <span className="text-2xl sm:text-3xl font-medium text-[#1C2A46]">
                      ₦{(price * quantity).toLocaleString()}
                    </span>
                    <div className="text-xs sm:text-sm text-[#6B635D] mt-1">
                      {quantity} set of magnet
                    </div>
                  </div>

                  <div className="flex-1">{renderAddToBagButton()}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Product Details */}
        <div className="text-left mt-12 sm:mt-16 md:mt-24 lg:mt-32 mb-6 sm:mb-8">
          <h3 className="text-2xl sm:text-3xl font-medium text-[#1C2A46] mb-6 sm:mb-8 border-b-2 pb-4 sm:pb-6 border-bordercolor">
            Product Details
          </h3>
          <p className="text-sm sm:text-base text-[#6B635D] mb-6 sm:mb-8 leading-relaxed">
            Erin-Ijesha Waterfalls (also known as Olumirin waterfalls) is
            located in Erin-odo, Osun State. It is a tourist attraction located
            in Oriade local government area, Osun State, Nigeria. The waterfalls
            were discovered in 1140 AD by one of the daughters of Oduduwa.
          </p>

          <h4 className="text-lg sm:text-xl font-medium text-[#1C2A46] mb-3 sm:mb-4">
            Specifications
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-8 text-sm">
            <div>
              <div className="flex justify-between py-3 sm:py-4 border-b-2 border-[#F0F0F0]">
                <span className="text-[#1C2A46] text-sm sm:text-base">
                  Magnet Count
                </span>
                <span className="text-[#1C2A46] text-sm sm:text-base font-medium">
                  6 pieces
                </span>
              </div>
              <div className="flex justify-between py-3 sm:py-4 border-b-2 border-[#F0F0F0]">
                <span className="text-[#1C2A46] text-sm sm:text-base">
                  Size
                </span>
                <span className="text-[#1C2A46] text-sm sm:text-base font-medium">
                  3" x 3" each
                </span>
              </div>
              <div className="flex justify-between py-3 sm:py-4 border-b border-bordercolor">
                <span className="text-[#1C2A46] text-sm sm:text-base">
                  Material
                </span>
                <span className="text-[#1C2A46] text-sm sm:text-base font-medium">
                  Premium vinyl
                </span>
              </div>
            </div>
            <div>
              <div className="flex justify-between py-3 sm:py-4 border-b-2 border-[#F0F0F0]">
                <span className="text-[#1C2A46] text-sm sm:text-base">
                  Finish
                </span>
                <span className="text-[#1C2A46] text-sm sm:text-base font-medium">
                  Glossy, water-resistant
                </span>
              </div>
              <div className="flex justify-between py-3 sm:py-4 border-b-2 border-[#F0F0F0]">
                <span className="text-[#1C2A46] text-sm sm:text-base">
                  Packaging
                </span>
                <span className="text-[#1C2A46] text-sm sm:text-base font-medium">
                  Gift box with ribbon
                </span>
              </div>
              <div className="flex justify-between py-3 sm:py-4 border-b border-bordercolor">
                <span className="text-[#1C2A46] text-sm sm:text-base">
                  Made In
                </span>
                <span className="text-[#1C2A46] text-sm sm:text-base font-medium">
                  Nigeria
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Reviews */}
        <CustomerReviews
          slug={PRODUCT_SLUG}
          onWriteReview={() => setShowReviewModal(true)}
          refreshKey={reviewsRefreshKey}
        />
      </div>

      {/* Modals */}
      <CustomizeModal
        isOpen={showCustomizeModal}
        onClose={() => setShowCustomizeModal(false)}
        onContinue={handleCustomizeContinue}
        onProgressChange={handleCustomizeProgress}
      />

      <PersonalizeModal
        isOpen={showPersonalizeModal}
        onClose={() => setShowPersonalizeModal(false)}
        onSave={handlePersonalizeSave}
        onProgressChange={handlePersonalizeProgress}
        customizationData={customizationData}
        isAdding={isAdding}
      />

      <ReviewModal
        isOpen={showReviewModal}
        onClose={() => {
          setShowReviewModal(false);
          setReviewsRefreshKey((k) => k + 1);
        }}
        product={{
          id: PRODUCT_SLUG,
          title: title,
          image: productImages[0],
          slug: PRODUCT_SLUG,
          price: price,
          category: 'Keepsake Album',
          description:
            'A one-page keepsake album that holds four removable memory magnets',
          inStock: inStock
        }}
      />

      <style jsx global>{`
        @keyframes fadeInDown {
          from {
            opacity: 0;
            transform: translate(-50%, -20px);
          }
          to {
            opacity: 1;
            transform: translate(-50%, 0);
          }
        }
      `}</style>
    </div>
  );
};

export default LegacyMagnetEdit;